/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {closeModal, closeSecondaryModal, openSecondaryModal} from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {Offer} from 'types/entities/Offer'
import {OfferStatus} from 'types/enums/OfferStatus'
import {OffersAction} from 'store/offers/OffersAction'
import {toast} from 'utils/toastUtils'
import UiFormField from 'components/Forms/UiFormField'
import {sanitizedInputs} from 'utils/utility'

import {ModalSize} from 'types/enums/ModalSize'
import UiAlert from 'components/Ui/UiAlert'

import LeadTaskForm from 'components/leads/forms/LeadTaskForm'
import LeadsAction from 'store/leads/LeadsAction'
import {Lead} from 'types/entities/Lead'

import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import UiOfferStatusSelect from 'components/Offers/UiOfferStatusSelect'
import UiTextarea from 'components/Ui/UiTextarea'
import OfferRejectionReasonsList from 'components/Offers/EditOfferStatusModalComponents/OfferRejectionReasonsList'

interface EditOfferStatusModalProps {
    offer: Offer
    onSuccess?: (offer: Offer) => void
    isSecondary?: boolean
    initialStatus?: OfferStatus
}

const EditOfferStatusModal: React.FC<EditOfferStatusModalProps> = ({
    offer,
    onSuccess,
    isSecondary,
    initialStatus,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const isStatusLockedToRejected = initialStatus === OfferStatus.Rejected
    const [status, setStatus] = useState<OfferStatus>(initialStatus ?? offer.status)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showRejectionConfirmation, setShowRejectionConfirmation] = useState(false)

    // Rejection reasons state
    const [selectedReasons, setSelectedReasons] = useState<string[]>(offer.extra?.rejection_reasons ?? [])
    const [otherReasonComment, setOtherReasonComment] = useState(offer.extra?.rejection_other_comment ?? '')
    const [rejectionComment, setRejectionComment] = useState(offer.extra?.rejection_comment ?? '')

    const isOtherReasonSelected = selectedReasons.includes('other')

    const handleSave = async (closeAfterSave: boolean = false) => {
        if (status === OfferStatus.Rejected) {
            if (selectedReasons.length === 0) {
                toast.warning(t('Please select at least one reason for rejection'))
                return
            }
            if (isOtherReasonSelected && !otherReasonComment.trim()) {
                toast.warning(t('Mandatory comment for other reason'))
                return
            }
        }

        if (status === OfferStatus.Rejected && !showRejectionConfirmation && offer.status !== OfferStatus.Rejected) {
            setShowRejectionConfirmation(true)
            return
        }

        performSave(closeAfterSave)
    }

    const performSave = async (closeAfterSave: boolean) => {
        if (
            status === OfferStatus.Accepted &&
            offer.status !== OfferStatus.Accepted
        ) {
            return
        }

        setIsSubmitting(true)
        try {
            const payload = {
                ...offer,
                status: status,
                extra: {
                    ...offer.extra,
                    rejection_reasons: status === OfferStatus.Rejected ? selectedReasons : [],
                    rejection_other_comment: status === OfferStatus.Rejected ? otherReasonComment : '',
                    rejection_comment: status === OfferStatus.Rejected ? rejectionComment : '',
                },
            }
            const updatedOffer = await dispatch(OffersAction.updateOffer(sanitizedInputs(payload)))
            toast.success(t('Offer status updated'))

            if (closeAfterSave) {
                if (isSecondary) {
                    dispatch(closeSecondaryModal())
                } else {
                    dispatch(closeModal())
                }
            } else {
                setIsSubmitting(false)
            }

            if (onSuccess) onSuccess(updatedOffer)

            if (status === OfferStatus.Rejected && showRejectionConfirmation) {
                confirmTaskCreation(updatedOffer)
            }
        } catch (e: any) {
            console.error(e)
            toast.error(t('Failed to update status'))
            setIsSubmitting(false)
        }
    }

    const confirmTaskCreation = (updatedOffer: Offer) => {
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Follow-up Task')}
                    message={t('Do you want to create a follow-up task for this lead?')}
                    onConfirm={() => {
                        dispatch(closeSecondaryModal())
                        createTaskFlow(updatedOffer)
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    const createTaskFlow = async (updatedOffer: Offer) => {
        if (!updatedOffer.lead_id) {
            toast.warning(t('Cannot create task: Lead not found on offer'))
            return
        }

        try {
            const lead = await dispatch(LeadsAction.fetchLeadById({ id: updatedOffer.lead_id } as Lead))

            // We need to wait a bit or just open it.
            // Since AlertConfirmation closes itself, we can open the modal.
            dispatch(
                openSecondaryModal(
                    <LeadTaskForm
                        lead={lead}
                        onChange={() => {
                            toast.success(t('Follow-up task created'))
                            if (onSuccess) onSuccess(updatedOffer)
                        }}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Create Follow-up Task')
                )
            )
        } catch (e) {
            console.error(e)
            toast.error(t('Failed to load lead information'))
        }
    }

    const toggleReason = (reasonId: string) => {
        setSelectedReasons((prev) =>
            prev.includes(reasonId) ? prev.filter((id) => id !== reasonId) : [...prev, reasonId]
        )
    }

    return (
        <UiFormModalShell
            className="EditOfferStatusModal"
            title={isStatusLockedToRejected ? t('Rejection Details') : t('Edit Offer Status')}
            showHeader
            footer={
                <UiFormActionsBar
                    primaryText={showRejectionConfirmation ? t('Confirm & Save') : t('Save')}
                    onPrimary={() => handleSave(false)}
                    saveAndCloseText={t('Save and close')}
                    onSaveAndClose={() => handleSave(true)}
                    loading={isSubmitting}
                />
            }
        >
            <div className="p-4 flex flex-col gap-4">
                {!isStatusLockedToRejected && (
                    <UiFormInner>
                        <UiFormGrid cols={1}>
                            <UiFormField>
                                <UiOfferStatusSelect value={status} onChange={(v) => setStatus(v ?? OfferStatus.Draft)} />
                            </UiFormField>
                        </UiFormGrid>
                    </UiFormInner>
                )}

                {status === OfferStatus.Rejected && (
                    <div className={`OfferStatusRejected flex flex-col gap-5 ${!isStatusLockedToRejected ? 'border-t pt-4 dark:border-gray-800' : ''}`}>
                        <div className="flex flex-col gap-4">
                            <div className="text-base font-bold text-gray-900 dark:text-gray-50">
                                {t('Reasons for Rejection')}
                            </div>
                            <div className="space-y-2.5 pl-2">
                                <OfferRejectionReasonsList
                                    selectedReasons={selectedReasons}
                                    isOtherReasonSelected={isOtherReasonSelected}
                                    otherReasonComment={otherReasonComment}
                                    onToggleReason={toggleReason}
                                    onOtherReasonCommentChange={setOtherReasonComment}
                                />
                            </div>
                        </div>

                        <div className="border-t dark:border-gray-700 pt-5">
                            <div className="flex flex-col gap-3">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {t('Optional comment')}
                                </label>
                                <UiTextarea
                                    name="rejection_comment"
                                    placeholder={t(
                                        'Optional comment on the rejection (e.g., feedback, comparison, potential for improvement)'
                                    )}
                                    value={rejectionComment}
                                    onChange={(e) => setRejectionComment(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {showRejectionConfirmation && (
                    <UiAlert
                        variant="warning"
                        title={t('Offer Rejected')}
                        description={t(
                            'You are about to mark this offer as Rejected. Do you want to proceed and create a follow-up task?'
                        )}
                    />
                )}
            </div>
        </UiFormModalShell>
    )
}

export default EditOfferStatusModal
